import { useLayoutStore } from '@/stores/layoutStore';
import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useActionResultMessages } from '@/composables/useActionResultMessages';
import { useApplyChartStyle } from '@/composables/useApplyChartStyle';
import { INVALID_CHART_STYLE_RECID } from '@/composables/isValidChartStyleRecid';
import { ChartList } from '@/interfaces/chartList';
import { SimpleFrontendChart } from '@/interfaces/chart';

const CREATE_ACCOUNT_MODAL = 'upgradeAccountModal';
const UPGRADE_TO_EXTRA_MODAL = 'upgradeToExtraModal';
const UPGRADE_TO_PRO_MODAL = 'upgradeToProModal';
const CREATE_PRICE_ALERT_MODAL = 'createPriceAlertModal';
const SAVE_CHART_MODAL = 'saveChartModal';
const SAVE_CHART_AS_MODAL = 'saveChartAsModal';
const SAVE_CHART_STYLE_MODAL = 'saveChartStyleModal';
const SET_CHART_STYLE_AS_DEFAULT_MODAL = 'setChartStyleAsDefaultModal';
const CHART_STYLE_BUTTON_MODAL = 'chartStyleButtonModal';
const WORKBENCH_PREFERENCES_MODAL = 'workbenchPreferencesModal';
const DELETE_CHART_MODAL = 'deleteChartModal';
const DELETE_CHART_STYLE_MODAL = 'deleteChartStyleModal';
const EDIT_CHART_NAME_MODAL = 'editChartNameModal';
const EDIT_CHART_STYLE_NAME_MODAL = 'editChartStyleNameModal';
const CREATE_CHART_LIST_MODAL = 'createChartListModal';
const APPLY_CHART_STYLE_TO_CHARTS_MODAL = 'applyChartStyleToChartsModal';
const START_INTERACTIVE_TOUR_MODAL = 'startInteractiveTourModal';
const END_INTERACTIVE_TOUR_MODAL = 'endInteractiveTourModal';
const EMAIL_MODAL = 'emailModal';
const COMING_SOON_MODAL = 'comingSoonModal';
const LINK_TO_CHART_MODAL = 'linkToChartModal';

function closeModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: '' });
}

function launchCreateAccountModal() {
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();

  if (isLoggedIn.value) {
    return;
  }

  layoutStore.$patch({ modalType: CREATE_ACCOUNT_MODAL });
}

function launchUpgradeToExtraModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: UPGRADE_TO_EXTRA_MODAL });
}

function launchUpgradeToProModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: UPGRADE_TO_PRO_MODAL });
}

function launchCreatePriceAlertModal() {
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  layoutStore.$patch({ modalType: CREATE_PRICE_ALERT_MODAL });
}

function launchSaveChartModal() {
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  layoutStore.$patch({ modalType: SAVE_CHART_MODAL });
}

function launchSaveChartAsModal() {
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  layoutStore.$patch({ modalType: SAVE_CHART_AS_MODAL });
}

function launchSaveChartStyleModal() {
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  layoutStore.$patch({ modalType: SAVE_CHART_STYLE_MODAL });
}

function launchSetAsDefaultChartStyleModal() {
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  layoutStore.$patch({ modalType: SET_CHART_STYLE_AS_DEFAULT_MODAL });
}

function launchChartStyleButtonModal(chartStyleRecid?: number) {
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  savedChartsStore.$patch({ chartStyleRecidBeingConfiguredInChartStyleButtonModal: chartStyleRecid ?? INVALID_CHART_STYLE_RECID });
  layoutStore.$patch({ modalType: CHART_STYLE_BUTTON_MODAL });
}

function launchWorkbenchPreferencesModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: WORKBENCH_PREFERENCES_MODAL });
}

function launchDeleteChartModal(chartRecid: number) {
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  savedChartsStore.$patch({ recidOfChartBeingDeletedInModalView: chartRecid });
  layoutStore.$patch({ modalType: DELETE_CHART_MODAL });
}

function launchDeleteChartStyleModal(chartStyleRecid: number) {
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  savedChartsStore.$patch({ recidOfChartStyleBeingDeletedInModalView: chartStyleRecid });
  layoutStore.$patch({ modalType: DELETE_CHART_STYLE_MODAL });
}

function launchEditChartNameModal(chartRecid: number) {
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  savedChartsStore.$patch({ recidOfChartBeingEditedInModalView: chartRecid });
  layoutStore.$patch({ modalType: EDIT_CHART_NAME_MODAL });
}

function launchEditChartStyleNameModal(chartStyleRecid: number) {
  const layoutStore = useLayoutStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  savedChartsStore.$patch({ recidOfChartStyleBeingEditedInModalView: chartStyleRecid });
  layoutStore.$patch({ modalType: EDIT_CHART_STYLE_NAME_MODAL });
}

function launchCreateChartListModal() {
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  layoutStore.$patch({ modalType: CREATE_CHART_LIST_MODAL });
}

function launchStartInteractiveTourModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: START_INTERACTIVE_TOUR_MODAL });
}

function launchEndInteractiveTourModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: END_INTERACTIVE_TOUR_MODAL });
}

function launchApplyChartStyleToChartsModal(chartList: ChartList, charts: SimpleFrontendChart[]) {
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();
  const { addYellowActionResultMessage } = useActionResultMessages();
  const { setChartListAndChartsToApplyChartStyleTo } = useApplyChartStyle();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  if (chartList.isReadonly) {
    window.sc3WorkbenchLogger.error(`Failed to launch the ApplyChartStyleToCharts modal - chartList is readonly.`);

    addYellowActionResultMessage('We\'re sorry, but you\'re unable to modify this read-only ChartList or its charts.');
    return;
  }

  setChartListAndChartsToApplyChartStyleTo(chartList, charts);

  layoutStore.$patch({ modalType: APPLY_CHART_STYLE_TO_CHARTS_MODAL });
}

function launchEmailModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: EMAIL_MODAL });
}

function launchLinkToChartModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: LINK_TO_CHART_MODAL });
}

function launchComingSoonModal() {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ modalType: COMING_SOON_MODAL });
}

export function useModals() {
  return {
    CREATE_ACCOUNT_MODAL,
    UPGRADE_TO_EXTRA_MODAL,
    UPGRADE_TO_PRO_MODAL,
    CREATE_PRICE_ALERT_MODAL,
    SAVE_CHART_MODAL,
    SAVE_CHART_AS_MODAL,
    SAVE_CHART_STYLE_MODAL,
    SET_CHART_STYLE_AS_DEFAULT_MODAL,
    CHART_STYLE_BUTTON_MODAL,
    WORKBENCH_PREFERENCES_MODAL,
    DELETE_CHART_MODAL,
    DELETE_CHART_STYLE_MODAL,
    EDIT_CHART_NAME_MODAL,
    EDIT_CHART_STYLE_NAME_MODAL,
    CREATE_CHART_LIST_MODAL,
    APPLY_CHART_STYLE_TO_CHARTS_MODAL,
    START_INTERACTIVE_TOUR_MODAL,
    END_INTERACTIVE_TOUR_MODAL,
    EMAIL_MODAL,
    LINK_TO_CHART_MODAL,
    COMING_SOON_MODAL,
    closeModal,
    launchCreateAccountModal,
    launchUpgradeToExtraModal,
    launchUpgradeToProModal,
    launchCreatePriceAlertModal,
    launchSaveChartModal,
    launchSaveChartAsModal,
    launchSaveChartStyleModal,
    launchSetAsDefaultChartStyleModal,
    launchChartStyleButtonModal,
    launchWorkbenchPreferencesModal,
    launchDeleteChartModal,
    launchDeleteChartStyleModal,
    launchEditChartNameModal,
    launchEditChartStyleNameModal,
    launchCreateChartListModal,
    launchStartInteractiveTourModal,
    launchEndInteractiveTourModal,
    launchApplyChartStyleToChartsModal,
    launchEmailModal,
    launchLinkToChartModal,
    launchComingSoonModal
  };
}
